import { PaywallCustomizationContext } from "@circle-react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization/PaywallCustomizationProvider";
import type {
  CouponApplications,
  Paywall,
  PaywallCustomization,
} from "@circle-react/types";
import {
  type CheckoutSessionStep,
  checkoutSessionStepStatuses,
  type CheckoutSessionStepPaywallPrice,
} from "@circle-react/types/Paywalls/Checkout/Session";
import { compact } from "lodash";

import { useCheckoutSession } from "../../../../Checkout/contexts/CheckoutSessionContext";
import {
  getMainPaywallFromStep,
  getPaywallOffer,
  getStepsByStatus,
} from "../../../helpers/SessionStepQueryHelper";
import { PaywallDescription } from "./PaywallDescription";
const buildPaywallAndPrices = (
  steps: CheckoutSessionStep[],
  sessionSteps: CheckoutSessionStep[],
): Array<
  [
    number,
    Paywall,
    CheckoutSessionStepPaywallPrice,
    CouponApplications | undefined,
    PaywallCustomization | undefined,
    string | undefined,
  ]
> => {
  if (steps.length === 0) {
    return [];
  }

  return compact(
    steps.map(step => {
      if (!step) {
        return null;
      }

      const paywallOffered = getMainPaywallFromStep(step);
      const paywallOffer = getPaywallOffer(step);

      if (!paywallOffered) {
        return null;
      }

      const price = step.community_member_subscription
        ? step.community_member_subscription.paywall_price
        : step.community_member_charge?.paywall_price;

      if (!price) {
        return null;
      }
      const couponApplications =
        step?.community_member_subscription?.coupon_applications ||
        step?.community_member_charge?.coupon_applications;

      const currentPaywall = step.community_member_subscription
        ? sessionSteps
            .flatMap(candidate => candidate.offers)
            .find(
              offer =>
                offer.paywall.id ===
                step.community_member_subscription?.paywall_id,
            )?.paywall
        : undefined;
      const paywall =
        step.sequence === 0 && step.community_member_subscription
          ? currentPaywall
          : paywallOffered;

      if (!paywall) {
        return null;
      }

      const paywallCustomization =
        paywallOffer?.paywall_customization || paywall.paywall_customization;

      return [
        step.id,
        paywall,
        price,
        couponApplications,
        paywallCustomization,
        step.sequence === 0
          ? step.community_member_subscription?.paywall_display_name
          : undefined,
      ];
    }),
  );
};

export const PaywallList = () => {
  const { session } = useCheckoutSession();

  const completedSteps = getStepsByStatus(session?.steps || [], [
    checkoutSessionStepStatuses.completed,
  ]);

  if (completedSteps.length === 0) {
    return null;
  }

  const paywalls = buildPaywallAndPrices(completedSteps, session?.steps || []);

  return (
    <div
      className="grid grid-cols-1 divide-y border-b"
      data-testid="paywall_list"
    >
      {paywalls.map(
        ([
          stepId,
          paywallOffered,
          offerPrice,
          offerPriceCouponApplications,
          paywallCustomization,
          paywallDisplayName,
        ]) => (
          <PaywallCustomizationContext
            key={stepId}
            paywallCustomization={paywallCustomization}
          >
            <PaywallDescription
              paywall={paywallOffered}
              paywallDisplayName={paywallDisplayName}
              price={offerPrice}
              couponApplications={offerPriceCouponApplications}
            />
          </PaywallCustomizationContext>
        ),
      )}
    </div>
  );
};
